import { useState, memo, Fragment, useRef, useEffect } from "react"
import { formatDateISOToTh } from "../utilities/Format"
import { IoMdMore } from "react-icons/io"
import { Menu, Transition } from '@headlessui/react'
import { MdCancel, MdCheckCircle } from "react-icons/md"
import { OrderPicklist } from "./types"

interface CommentProps {
  comments : OrderPicklist[],
  onPost : (comment: string) => void,
  onActive? : (orderId: string, skCommentId: string, active: boolean, version: number) => void,
  readonly?: boolean
  hideTitle?: boolean
  username?: string
}

const Comment = ({ comments, onPost, hideTitle, onActive, username = '', readonly } : CommentProps) => {
  const [ comment, setComment ] = useState<string>('')
  const [ showAlert, setShowAlert ] = useState<boolean>(false)
  const [ invalid, setInvalid ] = useState<boolean>(false)
  const lastCommentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (showAlert && lastCommentRef.current) {
      lastCommentRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showAlert, comments]);

  return (
    <>
      <div className="max-w-xl py-4 mx-auto">
        <div className="flex gap-2">
          <input
            type="text"
            value={comment} onChange={(e) => setComment(e.currentTarget.value)}
            placeholder="Write a comment..."
            className={`flex-1 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500${invalid ? ' border-red-500':''}`}
            disabled={readonly}
          />
          <button
            title="Post Comment"
            className="px-4 py-2 text-white transition-colors bg-blue-500 rounded-lg hover:bg-blue-600"
            onClick={() => {
              if (!comment) {
                setInvalid(true)
                return
              }
              onPost(comment)
              setInvalid(false)
              setShowAlert(true)
              setComment('')
            }}
          >
            Comment
          </button>
        </div>
      </div>
      <div className="pb-4">
        {hideTitle ? null : <div className="mb-4 text-sm text-gray-500">
          {comments.length} Comments
        </div>}
        {comments.length === 0 ? <div className="flex justify-center"><span className="italic text-muted">ไม่มีความคิดเห็น</span></div> : null}
        <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          {comments.map((comment, idx) => (
            <div
              key={comment.id}
              ref={idx === comments.length - 1 ? lastCommentRef : null}
              className={` p-4 border rounded-lg${showAlert && idx === comments.length - 1 ? ' border-green-500':''}${!defaultActive(comment.commentActive) ? ' opacity-30':''}`}>
              <div className="flex justify-between gap-2">
                <div className="flex items-center gap-4 ">
                <div className="flex items-center justify-center w-6 h-6 text-xs bg-gray-200 rounded-full">
                  {idx + 1}
                </div>
                  <div>
                    <div className="font-semibold">{comment.commentedBy}</div>
                    <div className="text-xs text-gray-500">{formatDateISOToTh(comment.createdAt)}</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  {defaultActive(comment.commentActive) ? null : <div
                    className={`text-xs px-2 h-fit py-1 rounded text-gray-600 bg-gray-100`}
                  >
                    ยกเลิก
                  </div>}
                  {comment.commentedBy === username && <Menu as="div" className="relative inline-block text-left">
                    <div>
                      <Menu.Button className="py-1 text-gray-400 hover:text-gray-600 h-fit">
                        <IoMdMore size={16} />
                      </Menu.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 w-24 mt-2 origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black/5 focus:outline-none">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={onActive ? () => onActive(comment.pk, comment.sk, !defaultActive(comment.commentActive), comment._version || 1) : undefined}
                              className={`${
                                active ? 'bg-blue-500 text-white' : 'text-gray-900'
                              } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                            >
                              {defaultActive(comment.commentActive) ? (
                                <MdCancel
                                  className="w-5 h-5 mr-2"
                                  aria-hidden="true"
                                />
                              ) : (
                                <MdCheckCircle
                                  className="w-5 h-5 mr-2"
                                  aria-hidden="true"
                                />
                              )}
                              {defaultActive(comment.commentActive) ? 'ยกเลิก' : 'เปิด'}
                            </button>
                          )}
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                  }
                </div>
              </div>
              <div className="mt-4 break-words">{comment.commentText}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

const defaultActive = (active?: boolean | null) => active === undefined || active === null ? true : active

export default memo(Comment)